import { IconButton } from "@material-ui/core";
import { AttachMoney, MoneyOff } from "@material-ui/icons";
import React, { useState } from "react";
import { Route, Switch } from "react-router-dom";

import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { useOrg } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { usePriceFiels } from "components/PriceFieldProvider";
import { useSyncedState } from "hooks/useSyncedState";
import { contactInfoFrom } from "models/ContactInfo";
import { emailFrom } from "models/Email";
import { DbDoc, WithRef } from "models/FbDoc";
import { IdMap } from "models/IdMap";
import { nameFrom } from "models/Name";
import { Offer } from "models/Offer";
import { OfferRoom } from "models/OfferRoom";
import { phoneFrom } from "models/Phone";
import { PriceField } from "models/PriceField";
import { PriceFieldCategories } from "models/PriceFieldCategories";
import { Rooms } from "models/Rooms";
import { Paths } from "Paths";
import { hasChanges } from "screens/PriceField/PriceFieldScreen";
import { calculateFinalPrice } from "Util/calculatePrices";
import { isEmpty } from "Util/isEmpty";
import { isNumber } from "Util/isNumber";

import { offerNavigation, OfferRoomCreationScreen } from "./OfferRoomCreationScreen";
import { OfferRoomEditor, PriceFieldGroups } from "./OfferRoomEditor";
import { OfferSummaryScreen } from "./OfferSummaryScreen";
import { getSubTotal } from "./SummaryTable";

type Props = {
    offer?: Offer
    path?: string
    offerDoc?: WithRef<DbDoc<Offer>>
}

function setIfDefined<T>(setValue: (arg: T) => void, value?: T) {
    if (value !== undefined) {
        setValue(value)
    }
}

const defaultHideUnitPrices = true

function groupPriceFieldsByRoomId(priceFields: IdMap<PriceField>): PriceFieldGroups {
    const priceFieldList = Object.values(priceFields)
    return Rooms.FlatList.reduce((acc, room) => {
        const roomCategories = Object.keys(room.categories.map) as PriceFieldCategories.Type[]
        return {
            ...acc,
            [room.id]: priceFieldList.filter(priceField => roomCategories.some(category => priceField.categories.map[category]))
        }
    }, {})
}

function updateOrRemove<T extends { id: string }>(object: IdMap<T>, value: T, replacement: T | null): IdMap<T> {
    const { [value.id]: old, ...rest } = object;
    if (replacement === null) {
        return rest;
    }
    return { ...rest, [replacement.id]: replacement };
}

export function CreateOffer({ offer, offerDoc, path = Paths.CREATE_OFFER }: Props) {
    const [priceFields, loadingPriceFields] = usePriceFiels()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [orgDoc, loadingOrg] = useOrg()

    const org = orgDoc?.data || null
    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [randId] = useState(Math.random().toString())
    const [emptyObject] = useState({})
    const [id] = useSyncedState(offer?.id || randId);
    const [discount, setDiscount] = useSyncedState(isEmpty(offer?.price.discount) ? '' : String(offer?.price.discount));
    const [manualPrice, setManualPrice] = useSyncedState(isEmpty(offer?.price.manualPrice) ? '' : String(offer?.price.manualPrice));
    const [offerRooms, setOfferRooms] = useSyncedState<IdMap<OfferRoom>>(offer?.offerRooms || emptyObject);
    const [offerRoomComments, setOfferRoomComments] = useSyncedState<{ [offerRoomId: string]: string }>(offer?.offerRoomComments || emptyObject);
    const [offerRoomNames, setOfferRoomNames] = useSyncedState<{ [offerRoomId: string]: string }>(offer?.offerRoomNames || emptyObject);
    const [customPriceFields, setCustomPriceFields] = useSyncedState<IdMap<PriceField>>(offer?.customPriceFields || emptyObject)

    const [comment, setComment] = useSyncedState(offer?.comment || '');
    const [firstName, setFirstName] = useSyncedState(offer?.contactInfo?.name?.firstName || '');
    const [lastName, setLastName] = useSyncedState(offer?.contactInfo?.name?.lastName || '');
    const [email, setEmail] = useSyncedState(offer?.contactInfo?.email?.email || '');
    const [phoneNumber, setPhoneNumber] = useSyncedState(offer?.contactInfo?.phone?.phoneNumber || '');
    const [address, setAddress] = useSyncedState(offer?.address || null);
    const [hideUnitPrices, setHideUnitPrices] = useSyncedState(offer?.hideUnitPrices ?? defaultHideUnitPrices);
    const [industryTypes] = useSyncedState(offer?.industryTypes.map || org?.industryTypes.map || emptyObject);

    const manualPriceAsNumber = manualPrice === '' ? NaN : Number(manualPrice)
    const discountAsNumber = Number(discount)

    const subTotal = getSubTotal(Object.values(offerRooms))

    const finalPrice = calculateFinalPrice({
        subTotal,
        discount: discountAsNumber,
        manualPrice: manualPriceAsNumber,
    })

    const isSaved = !!offer

    const loading = loadingOrgUser || loadingOrg || loadingAppUser || loadingPriceFields

    if (!orgUser || !appUser || !org || loading) {
        return <FullscreenLoading screenId="CreateOffer" />
    }

    const modifiedOffer: Offer = {
        id,
        org,
        price: {
            subTotal,
            discount: !isNumber(discountAsNumber) ? null : discountAsNumber,
            manualPrice: !isNumber(manualPriceAsNumber) ? null : manualPriceAsNumber,
            finalPrice,
        },
        hideUnitPrices,
        offerCreator: offer?.offerCreator || {
            appUser,
            orgUser,
        },
        comment,
        offerRoomComments,
        offerRoomNames,
        offerRooms,
        customPriceFields,
        address,
        contactInfo: contactInfoFrom(
            nameFrom(firstName, lastName),
            phoneFrom(phoneNumber),
            emailFrom(email),
        ),
        industryTypes: {
            map: industryTypes,
        }
    }

    const isChanged = isSaved ? (hasChanges(modifiedOffer, offer) || hasChanges(offer, modifiedOffer)) : (
        ((hideUnitPrices !== defaultHideUnitPrices) || !isEmpty([
            discount,
            manualPrice,
            modifiedOffer.comment,
            modifiedOffer.offerRoomComments,
            modifiedOffer.offerRoomNames,
            modifiedOffer.offerRooms,
            modifiedOffer.customPriceFields,
            modifiedOffer.address,
            modifiedOffer.contactInfo,
        ]))
    )

    const { contactInfo } = modifiedOffer

    const formIsValid = !!(
        contactInfo?.name?.fullName
        && (contactInfo?.email || contactInfo?.phone)
        && modifiedOffer.address?.addressString
    );

    return (
        <Switch>
            <Route exact path={path}>
                <OfferRoomCreationScreen
                    path={path}
                    isSaved={isSaved}
                    hasChanges={isChanged}
                    customPriceFields={customPriceFields}
                    setCustomPriceFields={setCustomPriceFields}
                    offerRoomComments={offerRoomComments}
                    setOfferRoomComments={setOfferRoomComments}
                    offerRoomNames={offerRoomNames}
                    setOfferRoomNames={setOfferRoomNames}
                    offerRooms={offerRooms}
                    setOfferRooms={setOfferRooms}
                    hideUnitPrices={hideUnitPrices}
                    setHideUnitPrices={setHideUnitPrices}
                    industries={industryTypes}
                />
            </Route>
            <Route path={path + '/sone/:offerRoomId'} render={(props) => {
                const { offerRoomId } = props.match.params
                const offerRoom = offerRoomId ? offerRooms[offerRoomId] : undefined

                const priceFieldGroups = priceFields ? groupPriceFieldsByRoomId(priceFields) : null

                return (
                    <PageNavigation
                        title={offerRoom ? ((offerRoomNames as any)[offerRoom.id] || offerRoom.room.name) : 'Fant ikke sonen'}
                        backHistoryButton
                        hasUnsavedChanges={isChanged}
                        safeUnsavedNavigation={[path]}
                        rightAppBar={
                            <IconButton color="inherit" aria-label="vis/gjem enhetspriser" onClick={() => setHideUnitPrices(!hideUnitPrices)}>
                                {hideUnitPrices ? <MoneyOff /> : <AttachMoney />}
                            </IconButton>
                        }
                        bottomNavigation={offerNavigation(path)}
                    >
                        {offerRoom ? (
                            <OfferRoomEditor
                                hideUnitPrices={hideUnitPrices}
                                priceFields={priceFields}
                                key={offerRoom.id}
                                offerRoom={offerRoom}
                                roomTag={Rooms.Flat[offerRoom.room.id]?.id}
                                priceFieldGroups={priceFieldGroups}
                                customPriceFields={customPriceFields}
                                industries={industryTypes}
                                setOfferRoom={newOfferRoom => {
                                    const updatedRooms = updateOrRemove(offerRooms, offerRoom, newOfferRoom)
                                    if (!updatedRooms[offerRoom.id]) {
                                        const { [offerRoom.id]: currentComment, ...otherComments } = offerRoomComments
                                        const { [offerRoom.id]: currentName, ...otherNames } = offerRoomNames
                                        setOfferRoomNames(otherNames)
                                        setOfferRoomComments(otherComments)
                                    }
                                    setOfferRooms(updatedRooms)
                                }}
                                name={(offerRoomNames as any)[offerRoom.id] || ''}
                                comment={(offerRoomComments as any)[offerRoom.id] || ''}
                                setComment={(comment) => {
                                    const { [offerRoom.id]: current, ...rest } = offerRoomComments
                                    setOfferRoomComments(isEmpty(comment) ? rest : {
                                        ...rest,
                                        [offerRoom.id]: comment,
                                    })
                                }}
                                setName={(name) => {
                                    const { [offerRoom.id]: current, ...rest } = offerRoomNames
                                    setOfferRoomNames(isEmpty(name) ? rest : {
                                        ...rest,
                                        [offerRoom.id]: name,
                                    })
                                }}
                            />
                        ) : null}
                    </PageNavigation>
                )
            }}>
            </Route>
            <Route path={path + '/send'}>
                <OfferSummaryScreen
                    path={path + '/send'}
                    isSaved={isSaved}
                    formIsValid={formIsValid}
                    hasChanges={isChanged}
                    phone={phoneNumber}
                    discount={discount}
                    manualPrice={manualPrice}
                    setOffer={offer => {
                        setIfDefined(setDiscount, offer.price?.discount)
                        setIfDefined(setManualPrice, offer.price?.manualPrice)
                        setIfDefined(setComment, offer.comment === null ? '' : offer.comment)
                        setIfDefined(setOfferRoomComments, offer.offerRoomComments)
                        setIfDefined(setOfferRooms, offer.offerRooms)
                        setIfDefined(setFirstName, offer.contactInfo?.name?.firstName)
                        setIfDefined(setLastName, offer.contactInfo?.name?.lastName)
                        setIfDefined(setPhoneNumber, offer.contactInfo?.phone)
                        setIfDefined(setEmail, offer.contactInfo?.email)
                        setIfDefined(setAddress, offer.address)
                        setIfDefined(setHideUnitPrices, offer.hideUnitPrices)
                    }}
                    offer={modifiedOffer}
                    offerDoc={offerDoc}
                />
            </Route>
        </Switch>
    )
}